import React, { useState, useEffect, useCallback } from 'react';
import {
  FileSignature, Plus, Filter, X, Eye, Pencil, Trash2, RefreshCw,
  CheckCircle, ChevronLeft, ChevronRight, ArrowRightLeft
} from 'lucide-react';
import { LoadingSpinner } from '../../UI';
import {
  C, FONT_UI, FONT_DATA, Mono, Card, Pill, PageHeader, PrimaryButton, GhostButton,
  IconButton, Toolbar, StatCard, Table, TH, TD, EmptyState, Banner, fmtNum,
} from './theme';
import { quotationAPI, clientAPI } from '../../../services';
import { useAuth } from '../../../context/AuthContext';
import LineItemsEditor from './billing/LineItemsEditor';
import { emptyLineItem, formatCurrency, formatDate, toDateInput } from './billing/lineItems';

const STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-800' },
  { value: 'sent', label: 'Sent', color: 'bg-blue-100 text-blue-800' },
  { value: 'accepted', label: 'Accepted', color: 'bg-green-100 text-green-800' },
  { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-800' },
  { value: 'expired', label: 'Expired', color: 'bg-amber-100 text-amber-800' },
  { value: 'converted', label: 'Converted', color: 'bg-purple-100 text-purple-800' }
];

const statusMeta = (status) => STATUSES.find((s) => s.value === status) || STATUSES[0];

// Maps the quotation lifecycle onto the shared Pill tones.
const STATUS_TONE = {
  draft: 'neutral',
  sent: 'info',
  accepted: 'success',
  rejected: 'danger',
  expired: 'warning',
  converted: 'purple',
};

// 'converted' is reached only through the convert action, never picked in the form.
const EDITABLE_STATUSES = STATUSES.filter((s) => s.value !== 'converted');

const emptyForm = () => ({
  title: '',
  client: '',
  prospectName: '',
  prospectCompany: '',
  prospectEmail: '',
  prospectPhone: '',
  items: [emptyLineItem()],
  discount: 0,
  issueDate: new Date().toISOString().split('T')[0],
  validUntil: '',
  status: 'draft',
  notes: '',
  terms: ''
});

const QuotationsPage = () => {
  const { user } = useAuth();
  // platform_staff only get the actions their platformPermissions grant; superadmin
  // (and anyone else who can reach this page) keeps full access.
  const canManage = (action) =>
    user?.role !== 'platform_staff' || user?.platformPermissions?.quotations?.[action] === true;
  const canCreateInvoice =
    user?.role !== 'platform_staff' || user?.platformPermissions?.billing?.create === true;

  const [quotations, setQuotations] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const [filters, setFilters] = useState({ search: '', status: '', client: '', startDate: '', endDate: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [converting, setConverting] = useState(null);
  const [convertDueDate, setConvertDueDate] = useState('');
  const [form, setForm] = useState(emptyForm());

  const fetchQuotations = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: pagination.limit };
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      const response = await quotationAPI.getQuotations(params);
      setQuotations(response.data.quotations || []);
      setPagination(response.data.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
    } catch (err) {
      setError(err.message || 'Failed to load quotations');
    } finally {
      setLoading(false);
    }
  }, [page, filters, pagination.limit]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await quotationAPI.getStats();
      setStats(response.data);
    } catch (err) {
      // Stats are supplementary - a failure here shouldn't blank the page.
      console.error('Failed to load quotation stats:', err.message);
    }
  }, []);

  useEffect(() => {
    fetchQuotations();
  }, [fetchQuotations]);

  useEffect(() => {
    fetchStats();
    clientAPI
      .getClients()
      .then((res) => setClients(res.data.clients || []))
      .catch((err) => console.error('Failed to load clients:', err.message));
  }, [fetchStats]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setIsModalOpen(true);
  };

  const openEdit = (quotation) => {
    setEditingId(quotation._id);
    setForm({
      title: quotation.title || '',
      client: quotation.client?._id || quotation.client || '',
      prospectName: quotation.prospect?.name || '',
      prospectCompany: quotation.prospect?.company || '',
      prospectEmail: quotation.prospect?.email || '',
      prospectPhone: quotation.prospect?.phone || '',
      items: quotation.items?.length ? quotation.items : [emptyLineItem()],
      discount: quotation.discount || 0,
      issueDate: toDateInput(quotation.issueDate),
      validUntil: toDateInput(quotation.validUntil),
      status: quotation.status || 'draft',
      notes: quotation.notes || '',
      terms: quotation.terms || ''
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.client && !form.prospectName.trim() && !form.prospectCompany.trim()) {
      setError('Select a client or enter prospect details');
      return;
    }
    if (!form.items.some((i) => i.description.trim())) {
      setError('Add at least one line item with a description');
      return;
    }

    const payload = {
      title: form.title,
      client: form.client || null,
      prospect: form.client
        ? undefined
        : {
            name: form.prospectName,
            company: form.prospectCompany,
            email: form.prospectEmail,
            phone: form.prospectPhone
          },
      items: form.items.filter((i) => i.description.trim()),
      discount: Number(form.discount) || 0,
      issueDate: form.issueDate,
      validUntil: form.validUntil || undefined,
      status: form.status,
      notes: form.notes,
      terms: form.terms
    };

    try {
      setSaving(true);
      if (editingId) {
        await quotationAPI.updateQuotation(editingId, payload);
        setSuccess('Quotation updated');
      } else {
        await quotationAPI.createQuotation(payload);
        setSuccess('Quotation created');
      }
      setIsModalOpen(false);
      fetchQuotations();
      fetchStats();
    } catch (err) {
      setError(err.message || 'Failed to save quotation');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (quotation) => {
    if (!window.confirm(`Delete quotation ${quotation.quoteNumber}? This cannot be undone.`)) return;
    try {
      await quotationAPI.deleteQuotation(quotation._id);
      setSuccess('Quotation deleted');
      fetchQuotations();
      fetchStats();
    } catch (err) {
      setError(err.message || 'Failed to delete quotation');
    }
  };

  const handleConvert = async (e) => {
    e.preventDefault();
    if (!convertDueDate) {
      setError('Due date is required');
      return;
    }
    try {
      setSaving(true);
      const response = await quotationAPI.convertToInvoice(converting._id, { dueDate: convertDueDate });
      setSuccess(`Invoice ${response.data.invoiceNumber} created from ${converting.quoteNumber}`);
      setConverting(null);
      setConvertDueDate('');
      fetchQuotations();
      fetchStats();
    } catch (err) {
      setError(err.message || 'Failed to convert quotation');
    } finally {
      setSaving(false);
    }
  };

  const recipientOf = (q) => q.client?.name || q.prospect?.company || q.prospect?.name || 'Unknown';

  return (
    <div style={{ fontFamily: FONT_UI }}>
      <PageHeader
        title="Quotations"
        subtitle="Proposals sent before anything is billed — Convert to Invoice is the only thing that turns one into a renewal or a new client invoice"
        actions={
          <>
            <GhostButton icon={RefreshCw} onClick={() => { fetchQuotations(); fetchStats(); }}>
              Refresh
            </GhostButton>
            {canManage('create') && (
              <PrimaryButton icon={Plus} onClick={openCreate}>
                New quotation
              </PrimaryButton>
            )}
          </>
        }
      />

      <Banner tone="error" message={error} onClose={() => setError('')} />
      <Banner tone="success" message={success} onClose={() => setSuccess('')} />

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          <StatCard label="Total quotations" value={fmtNum(stats.totalCount)} />
          <StatCard label="Pipeline value" value={formatCurrency(stats.totalValue)} />
          <StatCard label="Win rate" value={`${stats.winRate}%`} />
          <StatCard
            label="Awaiting response"
            value={fmtNum(stats.byStatus?.find((s) => s._id === 'sent')?.count || 0)}
          />
        </div>
      )}

      <Toolbar
        value={filters.search}
        onChange={(v) => { setPage(1); setFilters({ ...filters, search: v }); }}
        placeholder="Search by number, title or company..."
      >
        <GhostButton icon={Filter} onClick={() => setShowFilters(!showFilters)}>
          Filters
        </GhostButton>
      </Toolbar>

      {showFilters && (
        <Card className="p-4 mb-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <select
              value={filters.status}
              onChange={(e) => { setPage(1); setFilters({ ...filters, status: e.target.value }); }}
              className="px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{ border: `1px solid ${C.border}`, color: C.ink }}
            >
              <option value="">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <select
              value={filters.client}
              onChange={(e) => { setPage(1); setFilters({ ...filters, client: e.target.value }); }}
              className="px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{ border: `1px solid ${C.border}`, color: C.ink }}
            >
              <option value="">All clients</option>
              {clients.map((c) => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => { setPage(1); setFilters({ ...filters, startDate: e.target.value }); }}
              className="px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{ border: `1px solid ${C.border}`, color: C.ink, fontFamily: FONT_DATA }}
            />
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => { setPage(1); setFilters({ ...filters, endDate: e.target.value }); }}
              className="px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{ border: `1px solid ${C.border}`, color: C.ink, fontFamily: FONT_DATA }}
            />
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="lg" />
          </div>
        ) : quotations.length === 0 ? (
          <EmptyState
            icon={FileSignature}
            title="No quotations found"
            message="Proposals you create will appear here until they are converted."
          />
        ) : (
          <>
            <Table minWidth={980}>
              <thead>
                <tr>
                  <TH>Quote</TH>
                  <TH>Organization</TH>
                  <TH>Source</TH>
                  <TH>Title</TH>
                  <TH align="right">Total (incl. GST)</TH>
                  <TH>Valid until</TH>
                  <TH>Status</TH>
                  <TH align="right">Actions</TH>
                </tr>
              </thead>
              <tbody>
                {quotations.map((q) => (
                  <tr key={q._id} className="hover:bg-slate-50">
                    <TD>
                      <Mono className="font-medium">{q.quoteNumber}</Mono>
                    </TD>
                    <TD>{recipientOf(q)}</TD>
                    <TD>
                      <Pill tone={q.client ? 'success' : 'purple'}>
                        {q.client ? 'Existing client' : 'From lead'}
                      </Pill>
                    </TD>
                    <TD className="max-w-xs truncate" style={{ color: C.textMuted }}>
                      {q.title}
                    </TD>
                    <TD align="right">
                      <Mono className="font-medium">{formatCurrency(q.total, q.currency)}</Mono>
                    </TD>
                    <TD>
                      <Mono style={{ color: C.textMuted }}>{formatDate(q.validUntil)}</Mono>
                    </TD>
                    <TD>
                      <Pill tone={STATUS_TONE[q.status] || 'neutral'}>{statusMeta(q.status).label}</Pill>
                    </TD>
                    <TD align="right">
                      <div className="flex items-center justify-end gap-1">
                        {canManage('update') && canCreateInvoice && q.status === 'accepted' && q.client && (
                          <button
                            onClick={() => { setConverting(q); setConvertDueDate(''); }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-white mr-1"
                            style={{ background: C.ink }}
                          >
                            <ArrowRightLeft className="w-3.5 h-3.5" /> Convert to Invoice
                          </button>
                        )}
                        <IconButton icon={Eye} title="View" onClick={() => setViewing(q)} />
                        {canManage('update') && q.status !== 'converted' && (
                          <IconButton icon={Pencil} title="Edit" onClick={() => openEdit(q)} />
                        )}
                        {canManage('delete') && q.status !== 'converted' && (
                          <IconButton icon={Trash2} title="Delete" tone={C.rose} onClick={() => handleDelete(q)} />
                        )}
                      </div>
                    </TD>
                  </tr>
                ))}
              </tbody>
            </Table>

            {pagination.pages > 1 && (
              <div
                className="flex items-center justify-between px-5 py-3"
                style={{ borderTop: `1px solid ${C.border}` }}
              >
                <Mono className="text-[12px]" style={{ color: C.textMuted }}>
                  Page {pagination.page} of {pagination.pages} · {pagination.total} quotations
                </Mono>
                <div className="flex items-center gap-2">
                  <IconButton
                    icon={ChevronLeft}
                    title="Previous page"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={pagination.page <= 1}
                  />
                  <IconButton
                    icon={ChevronRight}
                    title="Next page"
                    onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                    disabled={pagination.page >= pagination.pages}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Create / edit modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingId ? 'Edit Quotation' : 'New Quotation'}
              </h2>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  required
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. DigiHR Annual Subscription - Acme Ltd"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Existing Client</label>
                <select
                  value={form.client}
                  onChange={(e) => setForm({ ...form, client: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">-- Prospect (not yet a client) --</option>
                  {clients.map((c) => (
                    <option key={c._id} value={c._id}>{c.name} ({c.code})</option>
                  ))}
                </select>
              </div>

              {/* Prospect details only apply when no client is selected */}
              {!form.client && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                    <input
                      type="text"
                      value={form.prospectName}
                      onChange={(e) => setForm({ ...form, prospectName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                    <input
                      type="text"
                      value={form.prospectCompany}
                      onChange={(e) => setForm({ ...form, prospectCompany: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={form.prospectEmail}
                      onChange={(e) => setForm({ ...form, prospectEmail: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="text"
                      value={form.prospectPhone}
                      onChange={(e) => setForm({ ...form, prospectPhone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Line Items *</label>
                <LineItemsEditor
                  items={form.items}
                  onChange={(items) => setForm({ ...form, items })}
                  discount={form.discount}
                  onDiscountChange={(discount) => setForm({ ...form, discount })}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date</label>
                  <input
                    type="date"
                    value={form.issueDate}
                    onChange={(e) => setForm({ ...form, issueDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valid Until</label>
                  <input
                    type="date"
                    value={form.validUntil}
                    onChange={(e) => setForm({ ...form, validUntil: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {EDITABLE_STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Terms &amp; Conditions</label>
                  <textarea
                    rows={3}
                    value={form.terms}
                    onChange={(e) => setForm({ ...form, terms: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Create Quotation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Read-only detail */}
      {viewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{viewing.quoteNumber}</h2>
                <p className="text-sm text-gray-500">{viewing.title}</p>
              </div>
              <button onClick={() => setViewing(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Recipient</p>
                  <p className="font-medium text-gray-900">{recipientOf(viewing)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Issued</p>
                  <p className="font-medium text-gray-900">{formatDate(viewing.issueDate)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Valid Until</p>
                  <p className="font-medium text-gray-900">{formatDate(viewing.validUntil)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusMeta(viewing.status).color}`}>
                    {statusMeta(viewing.status).label}
                  </span>
                </div>
              </div>

              <LineItemsEditor items={viewing.items || []} onChange={() => {}} discount={viewing.discount} currency={viewing.currency} disabled />

              {viewing.notes && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Notes</p>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{viewing.notes}</p>
                </div>
              )}
              {viewing.terms && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Terms &amp; Conditions</p>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{viewing.terms}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Convert to invoice */}
      {converting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Convert to Invoice</h2>
              <button onClick={() => setConverting(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleConvert} className="p-6 space-y-4">
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">
                  {converting.quoteNumber} ({formatCurrency(converting.total, converting.currency)}) will be issued to{' '}
                  {recipientOf(converting)} as a new draft invoice. The quotation becomes read-only.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Due Date *</label>
                <input
                  type="date"
                  required
                  value={convertDueDate}
                  onChange={(e) => setConvertDueDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConverting(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
                >
                  {saving ? 'Converting...' : 'Create Invoice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationsPage;
